import React from 'react'
import { ViewProps, Image, StyleSheet, View } from 'react-native'
import { AccessType, ProductPeripheralsState, Workspace, useProductTypes } from 'zens-store'
import { Color, Spacing, Text } from 'zens-storybook'
import { ProductConnectivity, ProductPeripherals } from 'zens-fragment'
import { Trans } from 'react-i18next'
import { usePermissions } from '@dms/hook'

import { Grid } from '../grid'

const styles = StyleSheet.create({
  container: {
    marginHorizontal: -Spacing.S,
  },
  content: {
    padding: 0,
  },
  product: {
    margin: Spacing.S,
  },
  top: {
    padding: Spacing.S,
    alignItems: 'center',
  },
  image: {
    width: 128,
    height: 128,
  },
  bottom: {
    marginHorizontal: -Spacing.S,
    borderTopWidth: 1,
    borderColor: Color.calmGrey,
    padding: Spacing.S,
  },
  states: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  state: {
    marginHorizontal: Spacing.XS,
  },
})

interface Props extends ViewProps {
  companyId: number
  workspace?: Workspace
}

const WorkspaceProduct = (props: Props) => {
  const { companyId, workspace, style, ...otherProps } = props
  const product = workspace?.product
  const connectivityState = workspace?.connectivityState
  const peripheralsState = workspace?.peripheralsState

  const { hasPermission } = usePermissions(companyId)
  const productTypes = useProductTypes()

  if (!product)
    return (
      <Text>
        <Trans>There are no devices connected to this workspace yet.</Trans>
      </Text>
    )

  function showPeripheralsState(peripheralsState?: number) {
    return (
      peripheralsState === ProductPeripheralsState.LedDisconnected ||
      peripheralsState === ProductPeripheralsState.SensorDisconnected ||
      peripheralsState === ProductPeripheralsState.SensorAndLedDisconnected
    )
  }

  function renderProduct() {
    const { typeId = 0, typeName, serialNumber } = product!
    const productType = productTypes.data?.find((type) => type.id === typeId)
    return (
      <View style={styles.product}>
        <View style={styles.top}>
          {productType && <Image source={{ uri: productType.imageUrl }} style={styles.image} resizeMode="contain" />}
          <View style={styles.states}>
            <ProductConnectivity value={connectivityState} />{' '}
            {showPeripheralsState(peripheralsState) && (
              <ProductPeripherals value={peripheralsState} style={styles.state} />
            )}
          </View>
        </View>
        <View style={styles.bottom}>
          <Text level={5}>{typeName}</Text>
          <Text>{serialNumber}</Text>
        </View>
      </View>
    )
  }

  return (
    <Grid
      style={[styles.container, style]}
      contentStyle={styles.content}
      renderItem={renderProduct}
      items={[
        {
          data: product,
          href: hasPermission(AccessType.Read, 'product')
            ? `/companies/${companyId}/products/${product.id}`
            : undefined,
        },
      ]}
      {...otherProps}
    >
      {renderProduct()}
    </Grid>
  )
}

export default WorkspaceProduct
